/**
 * Recently picked workspace objects, per organization and user, in this
 * browser's localStorage (ADR-001: the @-mention picker shows them under two
 * characters; 1c's search palette reuses them). A convenience only: every read
 * or write that storage refuses (private mode, quota, disabled) degrades to
 * "no recents" rather than failing the caller.
 */
import { useCallback, useState } from "react";

import { useAuth } from "@/contexts/AuthContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import { isWorkspaceObjectType, type WorkspaceObjectType } from "@/lib/workspace-objects";

export interface RecentObject {
  type: WorkspaceObjectType;
  id: string;
  label: string;
  /** Where it opened, when known (a note attached to a record opens on that record). */
  to?: string;
}

/** An in-app path; anything else read back from storage is dropped. */
const isAppPath = (to: unknown): to is string => typeof to === "string" && /^\/(?![/\\])/.test(to);

export const RECENTS_CAP = 8;

const storageKey = (organizationId: string, userId: string) =>
  `relay:recent-objects:v1:${organizationId}:${userId}`;

function isRecent(value: unknown): value is RecentObject {
  const row = value as Partial<RecentObject> | null;
  return !!row && typeof row.id === "string" && typeof row.label === "string"
    && typeof row.type === "string" && isWorkspaceObjectType(row.type)
    && (row.to === undefined || isAppPath(row.to));
}

export function readRecents(organizationId: string, userId: string): RecentObject[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(storageKey(organizationId, userId)) ?? "[]");
    return Array.isArray(parsed) ? parsed.filter(isRecent).slice(0, RECENTS_CAP) : [];
  } catch {
    return [];
  }
}

/** Moves `item` to the front, de-duplicated by type and id, capped. Returns the new list. */
export function pushRecent(organizationId: string, userId: string, item: RecentObject): RecentObject[] {
  const next = [
    { type: item.type, id: item.id, label: item.label, ...(isAppPath(item.to) ? { to: item.to } : {}) },
    ...readRecents(organizationId, userId).filter((r) => r.type !== item.type || r.id !== item.id),
  ].slice(0, RECENTS_CAP);
  try {
    localStorage.setItem(storageKey(organizationId, userId), JSON.stringify(next));
  } catch {
    // Storage refused the write; the in-memory list still reflects it.
  }
  return next;
}

/** Recents for the active organization and signed-in user; empty without either. */
export function useRecents() {
  const { organizationId } = useOrganization();
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const scope = organizationId && userId ? `${organizationId}:${userId}` : null;
  const [state, setState] = useState(() => ({
    scope,
    recents: organizationId && userId ? readRecents(organizationId, userId) : [],
  }));
  // Re-read when the org or user changes (derived state, not an effect).
  let recents = state.recents;
  if (state.scope !== scope) {
    recents = organizationId && userId ? readRecents(organizationId, userId) : [];
    setState({ scope, recents });
  }
  const push = useCallback(
    (item: RecentObject) => {
      if (!organizationId || !userId) return;
      setState({ scope, recents: pushRecent(organizationId, userId, item) });
    },
    [organizationId, userId, scope],
  );
  /** Re-read storage: another surface (the @ picker) may have recorded one since. */
  const refresh = useCallback(() => {
    setState({ scope, recents: organizationId && userId ? readRecents(organizationId, userId) : [] });
  }, [organizationId, userId, scope]);
  return { recents, push, refresh };
}
